import { Box, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { Link as RouterLink } from "react-router-dom";
import FMRLogo from "../../FMRLogo/FMRLogo";

const AuthFormContainer = ({
  FormComponent,
  pageTitle,
  pageDescription,
  additionalLink,
  isMobile,
}) => {
  return (
    <Box 
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        p: { xs: 2.5, md: 2, lg: 2 },   // reduced padding
        background: 'linear-gradient(135deg, rgba(0, 29, 61, 0.8) 0%, rgba(0, 8, 20, 0.9) 100%)',
        backdropFilter: 'blur(10px)',
        position: 'relative',
        overflow: 'hidden',
        height: '100%',
      }}
    >
      {/* Subtle background pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(0, 255, 136, 0.03) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(0, 153, 255, 0.03) 0%, transparent 50%)
          `,
          zIndex: 0,
        }}
      />

      {/* Centered Content Container */}
      <Box
        sx={{
          width: '100%',
          maxWidth: '400px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
          style={{
            position: 'relative',
            zIndex: 1,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {/* Logo Section */}
          <Box sx={{ textAlign: "center", mb: 3, width: "100%" }}>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{ display: "flex", justifyContent: "center" }}
            >
              <RouterLink to="/fmr" style={{ display: "inline-block" }}>
                <FMRLogo variant="auth" asLink={false} />
              </RouterLink>
            </motion.div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}>
              <Typography
                variant="h6"
                sx={{
                  mt: 1.5,
                  background: "linear-gradient(45deg, #fff 0%, #90caf9 100%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  fontWeight: 700,
                  letterSpacing: "0.05em",
                  textAlign: "center",
                  fontSize: "1.1rem",
                }}
              >
                FIND MY ROOMMATES
              </Typography>
            </motion.div>
          </Box>

          {/* Page Title */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            style={{ width: '100%', textAlign: 'center' }}
          >
            <Typography 
              variant="h3" 
              component="h1" 
              fontWeight="700" 
              gutterBottom
              sx={{
                color: 'white',
                textAlign: 'center',
                mb: 1.5,
                background: 'linear-gradient(45deg, #00ff88, #0099ff)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                width: '100%',
                fontSize: { xs: '1.8rem', md: '2rem' },  // reduced font size
              }}
            >
              {pageTitle}
            </Typography>
          </motion.div>
          
          {/* Page Description */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            style={{ width: '100%', textAlign: 'center' }}
          >
            <Typography 
              variant="body1" 
              sx={{
                color: 'rgba(255, 255, 255, 0.8)',
                textAlign: 'center',
                mb: 3,
                lineHeight: 1.5,
                fontSize: '1rem',   // reduced
                width: '100%',
              }}
            >
              {pageDescription}
            </Typography>
          </motion.div>
          
          {/* Form Component */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
          >
            <Box sx={{ width: '100%' }}>
              <FormComponent />
            </Box>
          </motion.div>
          
          {/* Additional Link */}
          {additionalLink && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.1 }}
              style={{ width: '100%', textAlign: 'center' }}
            >
              <Typography 
                variant="body2" 
                mt={2} 
                sx={{
                  mb: 2,
                  color: 'rgba(255, 255, 255, 0.7)',
                  width: '100%',
                  fontSize: '0.9rem',   // slightly smaller
                  '& a': {
                    color: '#00ff88',
                    textDecoration: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  },
                }}
              >
                {additionalLink}
              </Typography>
            </motion.div>
          )}
        </motion.div>
      </Box>
    </Box>
  );
};

export default AuthFormContainer;
